import React, { useState } from "react";
import RampsService from "../../services/RampsService";

/**
 * Cadastro da empresa no provedor de rampa, preenchido pelo próprio cliente.
 *
 * ── O que este formulário NÃO decide ──
 *
 * Nem o CNPJ nem a razão social. Os dois vêm do cadastro da empresa, no servidor:
 * o CNPJ é o da sessão (divergiu, o servidor recusa) e a razão social é a que a
 * Higher já conhece. Deixá-los editáveis aqui faria a conciliação entre os dois
 * lados depender de quem digitou melhor — e criaria a porta de cadastrar empresa
 * alheia.
 *
 * ── Beneficiários finais ──
 *
 * São obrigatórios, a menos que a empresa declare não ter nenhum. O servidor
 * recusa a lista vazia sem a declaração, e o formulário diz isso ANTES do envio:
 * descobrir no fim que faltava um sócio custa preencher tudo de novo.
 *
 * ── A validação de verdade mora no servidor ──
 *
 * Este formulário confere só o que evita ida e volta óbvia (campo vazio, e-mail
 * sem arroba). O contrato é um schema só, compartilhado com o cadastro que o
 * compliance envia, e duplicá-lo aqui criaria uma segunda regra que diverge da
 * primeira sem ninguém notar.
 */

const emptyAddress = {
  street_line_1: "",
  street_line_2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "BRA",
};

const emptyBeneficiary = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  date_of_birth: "",
  tin: "",
  country: "BRA",
  share_size: "",
  address: { ...emptyAddress },
};

function Field({ label, value, onChange, type = "text", placeholder, hint }) {
  return (
    <div className="onboarding__field">
      <label className="onboarding__label">{label}</label>
      <input
        className="onboarding__input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? <span className="onboarding__hint">{hint}</span> : null}
    </div>
  );
}

function AddressFields({ value, onChange }) {
  const set = (key) => (v) => onChange({ ...value, [key]: v });
  return (
    <div className="onboarding__grid">
      <Field label="Endereço" value={value.street_line_1} onChange={set("street_line_1")} />
      <Field label="Complemento" value={value.street_line_2} onChange={set("street_line_2")} />
      <Field label="Cidade" value={value.city} onChange={set("city")} />
      <Field label="Estado" value={value.state} onChange={set("state")} />
      <Field label="CEP" value={value.postal_code} onChange={set("postal_code")} />
      <Field
        label="País"
        value={value.country}
        onChange={set("country")}
        hint="Código ISO de 3 letras, ex. BRA"
      />
    </div>
  );
}

export default function RampOnboardingForm({ email, onSubmitted, onCancel }) {
  const [company, setCompany] = useState({
    date_of_incorporation: "",
    email: "",
    phone_number: "",
    tax_id: "",
    website: "",
    country: "BRA",
    address: { ...emptyAddress },
  });
  const [noUbos, setNoUbos] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([{ ...emptyBeneficiary }]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const setCompanyField = (key) => (value) => setCompany((prev) => ({ ...prev, [key]: value }));

  const setBeneficiary = (index, next) =>
    setBeneficiaries((prev) => prev.map((b, i) => (i === index ? next : b)));

  const addBeneficiary = () => setBeneficiaries((prev) => [...prev, { ...emptyBeneficiary }]);

  const removeBeneficiary = (index) =>
    setBeneficiaries((prev) => prev.filter((_, i) => i !== index));

  async function submit(event) {
    event.preventDefault();

    /*
      Só a conferência que evita ida e volta óbvia. O resto é do servidor, que tem o
      schema de verdade — e é o mesmo que valida o cadastro enviado pelo compliance.
    */
    if (!noUbos && beneficiaries.length === 0) {
      setError(
        "Informe ao menos um beneficiário final, ou marque que a empresa não tem nenhum.",
      );
      return;
    }

    setSending(true);
    setError("");
    try {
      const customer = {
        type: "business",
        // A razão social vai do cadastro, no servidor. Mandamos vazio de propósito:
        // o servidor substitui pelo nome que a Higher conhece.
        business_legal_name: "",
        date_of_incorporation: company.date_of_incorporation,
        email: company.email,
        phone_number: company.phone_number,
        tax_id: company.tax_id,
        country: company.country,
        address: company.address,
        website: company.website,
        no_ubos: noUbos,
        beneficiaries: noUbos ? [] : beneficiaries,
      };

      await RampsService.submitRampOnboarding(email, customer);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      // A frase vem do servidor e nomeia o campo. Trocá-la por um texto genérico
      // deixaria o cliente repetindo o mesmo erro.
      setError(
        err?.response?.data?.message ||
          "Não foi possível enviar o cadastro. Confira os campos e tente de novo.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="card onboarding">
      <style>{`
        .onboarding__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        .onboarding__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .onboarding__label { font-size: 12px; font-weight: bold; color: var(--muted); text-transform: uppercase; }
        .onboarding__input { border: 1px solid var(--border); border-radius: 4px; min-height: 38px; padding: 0 10px; font-size: 14px; box-sizing: border-box; width: 100%; }
        .onboarding__hint { font-size: 11px; color: var(--muted); }
        .onboarding__block { border-top: 1px solid var(--border); margin-top: 18px; padding-top: 14px; }
        .onboarding__error { color: #b42318; font-size: 13px; font-weight: bold; }
        .onboarding__actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 18px; }
      `}</style>

      <h3 style={{ marginTop: 0 }}>Cadastro para operar rampas</h3>
      <p style={{ color: "var(--muted)" }}>
        É uma vez só. Depois da aprovação, a tela de operações fica liberada.
      </p>

      <form onSubmit={submit}>
        <div className="onboarding__grid">
          <Field label="CNPJ" value={company.tax_id} onChange={setCompanyField("tax_id")}
            hint="Tem de ser o CNPJ desta conta" />
          <Field label="Data de constituição" type="date"
            value={company.date_of_incorporation} onChange={setCompanyField("date_of_incorporation")} />
          <Field label="E-mail da empresa" type="email"
            value={company.email} onChange={setCompanyField("email")} />
          <Field label="Telefone" value={company.phone_number} onChange={setCompanyField("phone_number")}
            hint="Com DDI, ex. +5511999999999" />
          <Field label="Website" value={company.website} onChange={setCompanyField("website")}
            hint="Opcional" />
          <Field label="País" value={company.country} onChange={setCompanyField("country")}
            hint="Código ISO de 3 letras" />
        </div>

        <div className="onboarding__block">
          <h4>Endereço da empresa</h4>
          <AddressFields value={company.address} onChange={setCompanyField("address")} />
        </div>

        <div className="onboarding__block">
          <h4>Beneficiários finais</h4>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <input type="checkbox" checked={noUbos} onChange={(e) => setNoUbos(e.target.checked)} />
            <span>Esta empresa não tem beneficiário final a declarar</span>
          </label>

          {!noUbos
            ? beneficiaries.map((beneficiary, index) => (
                <div key={index} className="onboarding__block">
                  <div className="onboarding__grid">
                    <Field label="Nome" value={beneficiary.first_name}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, first_name: v })} />
                    <Field label="Sobrenome" value={beneficiary.last_name}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, last_name: v })} />
                    <Field label="E-mail" type="email" value={beneficiary.email}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, email: v })} />
                    <Field label="Telefone" value={beneficiary.phone_number}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, phone_number: v })}
                      hint="Com DDI" />
                    <Field label="Nascimento" type="date" value={beneficiary.date_of_birth}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, date_of_birth: v })} />
                    <Field label="CPF" value={beneficiary.tin}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, tin: v })} />
                    <Field label="Participação (%)" value={beneficiary.share_size}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, share_size: v })}
                      hint="Maior que 0 e até 100" />
                    <Field label="País" value={beneficiary.country}
                      onChange={(v) => setBeneficiary(index, { ...beneficiary, country: v })} />
                  </div>
                  <AddressFields
                    value={beneficiary.address}
                    onChange={(address) => setBeneficiary(index, { ...beneficiary, address })}
                  />
                  {beneficiaries.length > 1 ? (
                    <button type="button" onClick={() => removeBeneficiary(index)}
                      style={{ marginTop: 8 }}>
                      Remover este beneficiário
                    </button>
                  ) : null}
                </div>
              ))
            : null}

          {!noUbos ? (
            <button type="button" onClick={addBeneficiary} style={{ marginTop: 8 }}>
              Adicionar beneficiário
            </button>
          ) : null}
        </div>

        {error ? <p className="onboarding__error">{error}</p> : null}

        <div className="onboarding__actions">
          <button type="submit" className="btnSaveAndContinue" disabled={sending}>
            {sending ? "Enviando..." : "Enviar cadastro"}
          </button>
          {onCancel ? (
            <button type="button" onClick={onCancel} disabled={sending}>
              Cancelar
            </button>
          ) : null}
        </div>
      </form>
    </section>
  );
}
